import type { Building } from "building-types/building/buildingTypes";
import type { BuiltInDataTableSettings } from "building-types/dataSequences/scheduleSettingsTable";
import type { Doc } from "building-types/doc";
import type {
  EntitySettings,
  EntityTypesEligibleForEntitySettings,
} from "building-types/entitySettings/entitySettingsTypes";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { CaseworkCatalogSettings } from "building-types/settings/caseworkCatalogSettings";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { DoorCatalogSettings } from "building-types/settings/doorCatalogSettings";
import type { DoorWallFramingSettings } from "building-types/settings/doorWallFramingSettings";
import type { DoorWallWrappingSettings } from "building-types/settings/doorWallWrappingSettings";
import type { DrawingCatalogSettings } from "building-types/settings/drawingCatalogSettings";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { ElectricalSettings } from "building-types/settings/electricalSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { ExteriorPropSettings } from "building-types/settings/exteriorPropSettings";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import {
  settingsTypeToHomeSettingsField,
  type HomeSettingsRW,
  type PartialHomeSettings,
} from "building-types/settings/homeSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { InteriorPropSettings } from "building-types/settings/interiorPropSettings";
import type { InteriorWindowCasingSettings } from "building-types/settings/interiorWindowCasingSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { ProfileCatalogSettings } from "building-types/settings/profileCatalogSettings";
import type { RailingCatalogSettings } from "building-types/settings/railingCatalogSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import type { ScheduleSettings } from "building-types/settings/scheduleSettings";
import type { Settings } from "building-types/settings/settings";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import { SettingsType } from "building-types/settings/settingsType";
import type { StairCatalogSettings } from "building-types/settings/stairCatalogSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WindowCatalogSettings } from "building-types/settings/windowCatalogSettings";
import type { WindowMullionSettings } from "building-types/settings/windowMullionSettings";
import type { WindowShutterSettings } from "building-types/settings/windowShutterSettings";
import type { WindowWallFramingSettings } from "building-types/settings/windowWallFramingSettings";
import type { WindowWallWrappingSettings } from "building-types/settings/windowWallWrappingSettings";
import type { Table } from "common/data/table";
import arrayMerge from "common/utils/arrayMerge";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { getDoorHomeSettings } from "./elements/doors/doorHomeSettings";
import { getWindowHomeSettings } from "./elements/window/windowHomeSettings";
import { docEntityGetSortedChildrenFromTable } from "./entity/docEntityGetChildrenFromTable";
import { evalFormulas, resolveOptionsInFormula } from "./formula/evalFormulas";
import { ineffectualSettingsFieldsSet } from "./ineffectualSettingsFields";

export function mergeSettingsSet(
  finalSettings: HomeSettingsRW,
  settingsParentId: string,
  settingsTable: Table<SettingsFragment>,
  ignoreTypeSpecificMergeBehavior: boolean = false,
): HomeSettingsRW {
  const settingsInSet = docEntityGetSortedChildrenFromTable(settingsTable, settingsParentId);

  for (const settings of settingsInSet) {
    const homeSettingsField = settingsTypeToHomeSettingsField[settings.type] as keyof HomeSettingsRW;
    // don't merge ineffectual settings fields
    if (!hasEffectualSettingsFields(settings)) {
      continue;
    }

    if (ignoreTypeSpecificMergeBehavior) {
      (finalSettings as any)[homeSettingsField] = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      };
      continue;
    }

    // takes precedence over porch settings
    if (settings.type === SettingsType.REAR_PORCH_SETTINGS) {
      mergeRearPorchSettings(settings as RearPorchSettings, finalSettings, settingsTable, settingsInSet);
      continue;
    }

    mergeSettings(settings, finalSettings, homeSettingsField);
  }

  return finalSettings;
}

function mergeRearPorchSettings(
  settings: RearPorchSettings,
  finalSettings: HomeSettingsRW,
  settingsTable: Table<SettingsFragment>,
  settingsInSet: SettingsFragment[],
) {
  // front porch setting in the same set
  const settingsOfPorchType =
    settingsTable[settingsInSet.find((x) => x.type === SettingsType.PORCH_SETTINGS)?.id ?? ""];
  // rear porch setting in this set with any front porch setting in the same set as a source
  const mergedSettings = {
    // front porch setting in the same set (from above)
    ...settingsOfPorchType,
    // the rear porch setting
    ...settings,
  } as SettingsFragment;

  finalSettings.rearPorch = {
    // resolved front porch setting from previous settings sets
    ...finalSettings.porch,
    // resolved rear porch setting from previous settings sets
    ...finalSettings.rearPorch,
    // rear porch setting in this set with any front porch setting in the same set as a source (from above)
    ...mergedSettings,
    viewNotes: {
      // resolved front porch setting view notes from previous settings sets
      ...finalSettings.porch?.viewNotes,
      // resolved rear porch setting view notes from previous settings sets
      ...finalSettings.rearPorch?.viewNotes,
      // front porch setting view notes in the same set
      ...(settingsOfPorchType as PorchSettings)?.viewNotes,
      // rear porch setting view notes in the same set
      ...settings?.viewNotes,
    },
  } as RearPorchSettings;

  return finalSettings;
}

export function mergeSettings(
  settings: SettingsFragment,
  finalSettings: HomeSettingsRW,
  homeSettingsField: keyof HomeSettingsRW,
) {
  switch (settings.type) {
    case SettingsType.PORCH_SETTINGS: {
      finalSettings.porch = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.porch?.viewNotes, ...settings.viewNotes },
      } as PorchSettings;
      break;
    }
    case SettingsType.WINDOW_MULLION_SETTINGS: {
      finalSettings.windowMullions = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as WindowMullionSettings;
      break;
    }
    case SettingsType.ELECTRICAL_SETTINGS: {
      if (settings.mergeRules) {
        finalSettings.electrical = {
          ...(finalSettings[homeSettingsField] as Settings),
          ...settings,
          rules: { ...finalSettings.electrical?.rules, ...settings.rules },
        } as ElectricalSettings;
      } else {
        (finalSettings as any)[homeSettingsField] = {
          ...(finalSettings[homeSettingsField] as Settings),
          ...settings,
        };
      }

      break;
    }
    case SettingsType.COLUMN_CATALOG_SETTINGS: {
      finalSettings.column = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        columnTable: { ...finalSettings.column?.columnTable, ...settings.columnTable },
      } as ColumnCatalogSettings;

      break;
    }
    case SettingsType.PROFILE_CATALOG_SETTINGS: {
      if (settings.mergeCatalog) {
        finalSettings.profile = {
          ...(finalSettings[homeSettingsField] as Settings),
          ...settings,
          profileTable: { ...finalSettings.profile?.profileTable, ...settings.profileTable },
        } as ProfileCatalogSettings;
      } else {
        (finalSettings as any)[homeSettingsField] = {
          ...(finalSettings[homeSettingsField] as Settings),
          ...settings,
        };
      }
      break;
    }
    case SettingsType.STAIR_CATALOG_SETTINGS: {
      finalSettings.stair = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        stairSettingsTable: { ...finalSettings.stair?.stairSettingsTable, ...settings.stairSettingsTable },
      } as StairCatalogSettings;
      break;
    }
    case SettingsType.RAILING_CATALOG_SETTINGS: {
      finalSettings.railing = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        railingSettingsTable: { ...finalSettings.railing?.railingSettingsTable, ...settings.railingSettingsTable },
      } as RailingCatalogSettings;
      break;
    }
    case SettingsType.EXTERIOR_PROP_SETTINGS: {
      finalSettings.exteriorProps = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        propTypes: { ...finalSettings.exteriorProps?.propTypes, ...settings.propTypes },
      } as ExteriorPropSettings;
      break;
    }
    case SettingsType.INTERIOR_PROP_SETTINGS: {
      finalSettings.interiorProps = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        propTypes: { ...finalSettings.interiorProps?.propTypes, ...settings.propTypes },
      } as InteriorPropSettings;
      break;
    }
    case SettingsType.AWNING_SETTINGS: {
      finalSettings.awning = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.awning?.viewNotes, ...settings.viewNotes },
      } as AwningSettings;
      break;
    }
    case SettingsType.WINDOW_SHUTTER_SETTINGS: {
      finalSettings.windowShutters = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.windowShutters?.viewNotes, ...settings.viewNotes },
      } as WindowShutterSettings;
      break;
    }
    case SettingsType.WINDOW_FRAMING_SETTINGS: {
      finalSettings.windowFraming = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as WindowWallFramingSettings;
      break;
    }
    case SettingsType.WINDOW_WRAPPING_SETTINGS: {
      finalSettings.windowWrapping = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as WindowWallWrappingSettings;
      break;
    }
    case SettingsType.WAINSCOTING_SETTINGS: {
      finalSettings.wainscoting = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.wainscoting?.viewNotes, ...settings.viewNotes },
      } as WainscotingSettings;
      break;
    }
    case SettingsType.EXTERIOR_DOOR_CASING_SETTINGS: {
      finalSettings.exteriorDoorCasing = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.exteriorDoorCasing?.viewNotes, ...settings.viewNotes },
      } as ExteriorDoorCasingSettings;
      break;
    }
    case SettingsType.INTERIOR_DOOR_CASING_SETTINGS: {
      finalSettings.interiorDoorCasing = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as InteriorDoorCasingSettings;
      break;
    }
    case SettingsType.DOOR_FRAMING_SETTINGS: {
      finalSettings.doorFraming = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as DoorWallFramingSettings;
      break;
    }
    case SettingsType.DOOR_WRAPPING_SETTINGS: {
      finalSettings.doorWrapping = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as DoorWallWrappingSettings;
      break;
    }
    case SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS: {
      finalSettings.exteriorWindowCasing = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.exteriorWindowCasing?.viewNotes, ...settings.viewNotes },
      } as ExteriorWindowCasingSettings;
      break;
    }
    case SettingsType.EXTERIOR_TRIM_SETTINGS: {
      finalSettings.exteriorTrim = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        viewNotes: { ...finalSettings.exteriorTrim?.viewNotes, ...settings.viewNotes },
      } as ExteriorTrimSettings;
      break;
    }
    case SettingsType.DRAWING_CATALOG_SETTINGS: {
      finalSettings.drawingCatalog = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        drawings: { ...finalSettings.drawingCatalog?.drawings, ...settings.drawings },
      } as DrawingCatalogSettings;
      break;
    }
    case SettingsType.CASEWORK_CATALOG_SETTINGS: {
      finalSettings.caseworkCatalog = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        caseworkTypes: { ...finalSettings.caseworkCatalog?.caseworkTypes, ...settings.caseworkTypes },
      } as CaseworkCatalogSettings;
      break;
    }
    case SettingsType.DOOR_CATALOG_SETTINGS: {
      finalSettings.doorCatalog = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        doorTypes: { ...finalSettings.doorCatalog?.doorTypes, ...settings.doorTypes },
      } as DoorCatalogSettings;
      break;
    }
    case SettingsType.WINDOW_CATALOG_SETTINGS: {
      finalSettings.windowCatalog = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        windowTypes: { ...finalSettings.windowCatalog?.windowTypes, ...settings.windowTypes },
      } as WindowCatalogSettings;
      break;
    }
    case SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS: {
      finalSettings.electricalItemCatalog = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
        loadTypes: { ...finalSettings.electricalItemCatalog?.loadTypes, ...settings.loadTypes },
      } as ElectricalItemCatalogSettings;
      break;
    }

    case SettingsType.FOUNDATION_SETTINGS:
    case SettingsType.WALL_CATALOG_SETTINGS:
      (finalSettings as any)[homeSettingsField] = mergeFieldsAndShallowMergeContainers(
        finalSettings[homeSettingsField],
        settings,
      );
      break;

    case SettingsType.SCHEDULE_SETTINGS: {
      finalSettings.schedule = {
        ...finalSettings.schedule,
        scheduleSettingsTable: shallowMergeContainersByFilter(
          finalSettings.schedule?.scheduleSettingsTable,
          settings.scheduleSettingsTable,
          (setting) => (setting as BuiltInDataTableSettings).scheduleName,
        ),
      } as ScheduleSettings;
      break;
    }

    case SettingsType.INTERIOR_WINDOW_CASING_SETTINGS: {
      finalSettings.interiorWindowCasing = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      } as InteriorWindowCasingSettings;
      break;
    }

    default: {
      (finalSettings as any)[homeSettingsField] = {
        ...(finalSettings[homeSettingsField] as Settings),
        ...settings,
      };
    }
  }
}

type BuildingParams = Pick<Building, "settingsSets" | "optionChoices"> & { homeSettings: Building["settings"] };
type DocParams = Pick<
  Doc,
  "entitySettings" | "dormers" | "roofOpenings" | "openings" | "optionSets" | "instanceOptionSets"
> & { settings: Doc["settings"] };

export type MergeSettingsBuildingInput = Pick<Building, "settingsSets" | "optionChoices" | "settings">;
export type MergeSettingsDocInput = DocParams;
export type MergeSettingsEntityParamsInput = BuildingParams & DocParams;

export function buildMergeSettingsEntityParams(
  building: MergeSettingsBuildingInput,
  doc: MergeSettingsDocInput,
): MergeSettingsEntityParamsInput {
  return {
    settingsSets: building.settingsSets,
    optionChoices: building.optionChoices,
    homeSettings: building.settings,
    settings: doc.settings,
    entitySettings: doc.entitySettings,
    dormers: doc.dormers,
    roofOpenings: doc.roofOpenings,
    openings: doc.openings,
    optionSets: doc.optionSets,
    instanceOptionSets: doc.instanceOptionSets,
  };
}

type MergeSettingsFullBuildingInput = MergeSettingsBuildingInput & { doc: MergeSettingsDocInput };

export function getMergeSettingsForOpeningParamsFromBuilding(
  building: MergeSettingsFullBuildingInput,
): Omit<MergeSettingsForEntityParams, "homeSettings">;
export function getMergeSettingsForOpeningParamsFromBuilding(
  building: MergeSettingsBuildingInput,
  doc: MergeSettingsDocInput,
): Omit<MergeSettingsForEntityParams, "homeSettings">;
export function getMergeSettingsForOpeningParamsFromBuilding(
  building: MergeSettingsBuildingInput & { doc?: MergeSettingsDocInput },
  doc?: MergeSettingsDocInput,
): Omit<MergeSettingsForEntityParams, "homeSettings"> {
  const docParams = doc ?? building.doc;
  assertIsNotUndefined(docParams);
  return getMergeSettingsForEntityParamsFromBuilding(buildMergeSettingsEntityParams(building, docParams), "OPENING");
}

export function getMergeSettingsForEntityParamsFromBuilding<T extends EntityTypesEligibleForEntitySettings>(
  building: MergeSettingsEntityParamsInput,
  entityType: T,
): Omit<MergeSettingsForEntityParams, "homeSettings"> {
  const {
    settingsSets: activeSettingsSets,
    optionChoices,
    homeSettings,
    settings,
    entitySettings,
    dormers,
    roofOpenings,
    openings,
    optionSets,
    instanceOptionSets,
  } = building;

  const formulas: Partial<Doc> = { settings };

  switch (entityType) {
    case "DORMER": {
      const windowHomeSettings = getWindowHomeSettings(homeSettings);
      Object.assign(formulas, dormers, windowHomeSettings);
      break;
    }
    case "ROOF_OPENING": {
      const windowHomeSettings = getWindowHomeSettings(homeSettings);
      Object.assign(formulas, roofOpenings, windowHomeSettings);
      break;
    }
    case "OPENING": {
      const windowHomeSettings = getWindowHomeSettings(homeSettings);
      const doorHomeSettings = getDoorHomeSettings(homeSettings);
      const openingSettings = { ...windowHomeSettings, ...doorHomeSettings };

      Object.assign(formulas, openings, openingSettings);
      break;
    }
    case "ROOF":
      // TODO:
      break;
    case "ROOM":
      // TODO:
      break;
  }

  return {
    entitySettings,
    activeSettingsSets,
    settings,
    optionChoices: resolveOptionsInFormula(formulas, { optionSets, instanceOptionSets }, optionChoices),
  };
}

const entitySettingsByEntityIdCache = new WeakMap<Table<EntitySettings>, Map<string, EntitySettings[]>>();
const settingsByParentIdCache = new WeakMap<Table<SettingsFragment>, Map<string, SettingsFragment[]>>();

function getEntitySettingsByEntityId(entitySettings: Table<EntitySettings>): Map<string, EntitySettings[]> {
  const cached = entitySettingsByEntityIdCache.get(entitySettings);
  if (cached) {
    return cached;
  }

  const index = new Map<string, EntitySettings[]>();
  for (const id in entitySettings) {
    const entitySetting = entitySettings[id];
    const existing = index.get(entitySetting.entityId);
    if (existing) {
      existing.push(entitySetting);
    } else {
      index.set(entitySetting.entityId, [entitySetting]);
    }
  }

  for (const [, settingsForEntity] of index) {
    sortEntitiesBySortIndex(settingsForEntity);
  }

  entitySettingsByEntityIdCache.set(entitySettings, index);
  return index;
}

function getSettingsByParentId(settings: Table<SettingsFragment>): Map<string, SettingsFragment[]> {
  const cached = settingsByParentIdCache.get(settings);
  if (cached) {
    return cached;
  }

  const index = new Map<string, SettingsFragment[]>();
  for (const id in settings) {
    const setting = settings[id];
    const existing = index.get(setting.parentId);
    if (existing) {
      existing.push(setting);
    } else {
      index.set(setting.parentId, [setting]);
    }
  }

  settingsByParentIdCache.set(settings, index);
  return index;
}

export function mergeSettingsForEntity(
  entitySettings: MergeSettingsForEntityParams["entitySettings"],
  entityId: string | undefined,
  activeSettingsSets: MergeSettingsForEntityParams["activeSettingsSets"],
  settings: MergeSettingsForEntityParams["settings"],
  optionChoices: MergeSettingsForEntityParams["optionChoices"],
): PartialHomeSettings {
  if (!entityId) {
    return {};
  }

  const entitySettingsForThisEntity = getEntitySettingsByEntityId(entitySettings).get(entityId) ?? [];
  const settingsByParentId = getSettingsByParentId(settings);
  const combinedSettings: PartialHomeSettings = {};

  const mergeSettingForEntitySetting = (setting: SettingsFragment) => {
    if (!setting) {
      return;
    }

    const fieldName = settingsTypeToHomeSettingsField[setting.type];
    assertIsNotUndefined(fieldName);
    setting = evalFormulas(setting, (v) => optionChoices[v]);
    mergeSettings(setting, combinedSettings as HomeSettingsRW, fieldName);
  };

  for (const { settingsOrSettingsSetId } of entitySettingsForThisEntity) {
    // Entity Settings can either reference an individual Setting or a Settings Set
    const settingsSet = activeSettingsSets[settingsOrSettingsSetId];
    const setting = settings[settingsOrSettingsSetId];

    if (setting) {
      mergeSettingForEntitySetting(setting);
    } else if (settingsSet) {
      const childSettings = settingsByParentId.get(settingsSet.id);
      if (!childSettings) {
        continue;
      }

      for (const childSetting of childSettings) {
        mergeSettingForEntitySetting(childSetting);
      }
    }
  }

  return combinedSettings;
}

function shallowMergeContainersByFilter(a: any, b: any, filterKeyFn: (obj: any) => string): any {
  const result = {};
  const arrayCheck = (a?: any) => a && typeof a === "object" && Array.isArray(a);

  function addToResults(field: string) {
    const aval = a?.[field];
    const bval = b?.[field];
    if (arrayCheck(aval) && arrayCheck(bval)) {
      const mergeResult = {};
      for (const arrayEntry of aval) {
        mergeResult[filterKeyFn(arrayEntry)] = arrayEntry;
      }
      for (const arrayEntry of bval) {
        mergeResult[filterKeyFn(arrayEntry)] = arrayEntry;
      }
      result[field] = Object.values(mergeResult);
    } else if (bval) {
      result[field] = bval;
    } else if (aval) {
      result[field] = aval;
    }
  }

  for (const field in a) {
    addToResults(field);
  }
  for (const field in b) {
    if (!a?.[field]) {
      addToResults(field);
    }
  }
  return result;
}

function mergeFieldsAndShallowMergeContainers(a: any, b: any): any {
  const result = { ...a, ...b };

  for (const field in a) {
    const aval = a[field];
    const bval = b[field];

    if (typeof aval === "object" && aval !== null) {
      if (Array.isArray(aval)) {
        result[field] = arrayMerge(aval, bval);
      } else {
        result[field] = { ...aval, ...bval };
      }
    }
  }

  return result;
}

export function removeIneffectualSettingsFields(obj: SettingsFragment): Partial<Settings> {
  const result = {};
  for (const key in obj) {
    if (ineffectualSettingsFieldsSet.has(key as keyof SettingsFragment)) {
      continue;
    }

    result[key] = obj[key];
  }
  return result;
}

function hasEffectualSettingsFields(obj: SettingsFragment): boolean {
  for (const key in obj) {
    if (ineffectualSettingsFieldsSet.has(key as keyof SettingsFragment)) {
      continue;
    }

    return true;
  }
  return false;
}
